/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import * as logger from '../logger/logger';
import {GenericObject} from '../types/entities';

/**
 * Defines the types of Worker Logger implementations that can be used.
 */
export enum AbstractWorkerLoggerType {
  Client = 'Client',
  Script = 'Script',
  Service = 'Service'
}

/**
 * Provides the common Logger implementation for use by all Worker instances.
 */
export default class WorkerLogger {
  /**
   * Provides a reference to the type of logger implementation that is being
   * used.
   */
  private type: AbstractWorkerLoggerType;

  /**
   * Defines the context for which the logger is being used, the context is
   * generally the name of the Worker implementation.
   */
  private context: string;

  /**
   * The WorkerLogger constructor simply requires the context (name of the
   * Client or Script Worker Implementation), which is used to provide context
   * in the logs.
   *
   * @param type Defines AbstractWorkerLoggerType to be used when logging Worker
   *        messages.
   * @param context Defines the context for which the logger is being used,
   *        the context is generally the name of the Worker implementation.
   */
  constructor(type: AbstractWorkerLoggerType, context: string) {
    this.type = type;
    this.context = context;
  }

  /**
   * Provides the logger abstraction to which all logs are facilitated.
   *
   * @param message The message to be logged to the underlying logging facility.
   * @param isError When true, indicates that an error is to be logged, otherwise
   *        a informational log will be invoked (default).
   */
  public log = (message: string, isError = false) => {
    const title = `${this.context} ${this.type}`;
    const msg   = `${title}: ${message}.`;
    logger[!isError ? 'info' : 'error'](msg);
  }

  /**
   * Provides a convenience utility which allows for logging object properties.
   *
   * @param item The object whose keys and values are to be logged
   */
  public properties = (item: GenericObject) => {
    logger.values(...Object.entries(item));
  }
}
